<template>
  <!-- Plan decision 13: `GET /identity/token` answered TERMS_REQUIRED. The
       person accepts here, inside the tenant's own pre-auth page, or signs out.
       Same content as the Auth0 terms page (legacy-client TermsAndConditions.vue):
       the document in view, the commercial-agreement statement, a tick box that
       must be ticked before Accept, and a confirm step before Decline. -->
  <PreAuthPage card-class="terms-card">
    <div class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="44" />
      <h1 class="sh-card__title">{{ t("auth.identity.terms.title", { name: tenantName }) }}</h1>
      <p class="sh-card__subtitle">{{ t("auth.identity.terms.body") }}</p>
    </div>

    <!-- Only a PDF is shown in the page (shared @pdfviewer, as the Auth0 page's PdfViewer).
         A tenant terms page on another site is only linked. -->
    <div v-if="embeddable" class="terms__viewer" data-test="terms-viewer">
      <PdfViewer
        :source="documentUrl"
        :title="t('auth.identity.terms.document')"
        :download-name="`${tenantName}-Terms-and-Conditions.pdf`"
      />
    </div>

    <a class="terms__document" data-test="terms-document" :href="documentUrl" target="_blank" rel="noopener noreferrer">
      <v-icon size="16">description</v-icon>
      <span>{{ t(embeddable ? "auth.identity.terms.openDocument" : "auth.identity.terms.document") }}</span>
      <v-icon size="14" class="terms__external">open_in_new</v-icon>
    </a>

    <p class="terms__commercial" data-test="terms-commercial">{{ t("auth.identity.terms.commercial") }}</p>

    <label class="terms__agree" data-test="terms-agree">
      <input v-model="agreed" type="checkbox" :disabled="accepting" />
      <span>{{ t("auth.identity.terms.agree") }}</span>
    </label>

    <p v-if="failed" class="sh-card__error" role="alert">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ t("auth.identity.terms.failed") }}</span>
    </p>

    <template v-if="!confirmingDecline">
      <v-btn
        class="terms__accept"
        data-test="terms-accept"
        color="primary"
        size="large"
        variant="flat"
        block
        :disabled="!agreed"
        :loading="accepting"
        @click="onAccept"
        >{{ t("auth.identity.terms.accept") }}</v-btn>
      <v-btn
        class="terms__decline"
        data-test="terms-decline"
        variant="text"
        size="small"
        block
        :disabled="accepting"
        @click="openDeclineConfirm"
        >{{ t("auth.identity.terms.decline") }}</v-btn>
    </template>

    <!-- Was: Decline signed out at once. The Auth0 page asks first, so this asks first too. -->
    <div
      v-else
      class="terms__confirm"
      role="alertdialog"
      aria-modal="false"
      aria-labelledby="terms-decline-question"
      data-test="terms-decline-confirm"
    >
      <p id="terms-decline-question">{{ t("auth.identity.terms.declineConfirm", { name: tenantName }) }}</p>
      <div class="terms__confirm-actions">
        <v-btn ref="cancelBtn" variant="outlined" size="small" data-test="terms-decline-cancel" @click="closeDeclineConfirm">{{
          t("auth.identity.terms.declineCancel")
        }}</v-btn>
        <v-btn color="error" variant="flat" size="small" data-test="terms-decline-yes" @click="session.declineTerms()">{{
          t("auth.identity.terms.declineYes")
        }}</v-btn>
      </div>
    </div>
  </PreAuthPage>
</template>

<script lang="ts">
import { computed, defineAsyncComponent, defineComponent, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import { isPdfUrl, termsHref } from "@/tenant/loginLinks";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";

/**
 * The platform terms that version 2.0 names. Used only when the tenant has no terms document of its own.
 * First-party copy on metaship.ai (the public /terms page serves the same file).
 * Was: https://cdn.metaship.ai/terms_and_conditions.pdf. Browser extensions block that cdn.* request
 * (ERR_BLOCKED_BY_CLIENT), so a person could see no document on the step that asks them to accept it.
 */
const PLATFORM_TERMS_URL = "https://metaship.ai/terms_and_conditions.pdf";

// Moved to tenant/loginLinks.ts (the sign-in footer uses it too); re-exported for callers.
export { isPdfUrl };

export default defineComponent({
  name: "AcceptTerms",
  components: {
    LogoTile,
    PreAuthPage,
    // Lazy: pdf.js loads only when a person reaches the terms step, not with the sign-in page.
    PdfViewer: defineAsyncComponent(() => import("@pdfviewer").then((m) => m.PdfViewer)),
  },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    const tenantName = computed(() => session.tenant?.name || "Metaship");
    const branding = computed(() => session.tenant?.branding ?? null);
    const documentUrl = computed(() => termsHref(session.tenant?.loginPage) ?? PLATFORM_TERMS_URL);
    const embeddable = computed(() => isPdfUrl(documentUrl.value));
    const agreed = ref(false);
    const confirmingDecline = ref(false);
    const accepting = ref(false);
    const failed = ref(false);

    // Keyboard focus follows the confirm step: the Decline button is removed when it opens.
    const cancelBtn = ref<{ $el?: HTMLElement } | null>(null);
    function focusEl(selector: string): void {
      (document.querySelector(selector) as HTMLElement | null)?.focus();
    }
    async function openDeclineConfirm(): Promise<void> {
      confirmingDecline.value = true;
      await nextTick();
      cancelBtn.value?.$el?.focus();
    }
    async function closeDeclineConfirm(): Promise<void> {
      confirmingDecline.value = false;
      await nextTick();
      focusEl('[data-test="terms-decline"]');
    }

    async function onAccept(): Promise<void> {
      if (!agreed.value) return;
      accepting.value = true;
      failed.value = false;
      try {
        await session.acceptTerms();
      } catch {
        // Still on this step (the acceptance did not save) → say so. A failure
        // after the save moved the session on, and that screen shows its own error.
        if (session.status === "terms-required") failed.value = true;
      } finally {
        accepting.value = false;
      }
    }

    return {
      t,
      session,
      tenantName,
      branding,
      documentUrl,
      embeddable,
      agreed,
      confirmingDecline,
      cancelBtn,
      openDeclineConfirm,
      closeDeclineConfirm,
      accepting,
      failed,
      onAccept,
    };
  },
});
</script>

<style>
/* Not scoped: the card element belongs to PreAuthPage. The document needs room to read. */
.sh-card.terms-card {
  max-width: 760px;
}
</style>

<style scoped>
/* @pdfviewer needs a definite height on its host pane. */
.terms__viewer {
  display: flex;
  flex-direction: column;
  height: 56vh;
  max-height: 520px;
  margin: 0 0 10px;
  border: 1px solid var(--sh-hairline);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(var(--v-theme-on-surface), 0.04);
}
.terms__document {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  padding: 10px 12px;
  border: 1px solid var(--sh-hairline);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}
.terms__document:hover {
  text-decoration: underline;
}
.terms__external {
  margin-left: auto;
  opacity: 0.7;
}
.terms__commercial {
  margin: 0 0 12px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--sh-text-60);
}
.terms__agree {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.4;
  cursor: pointer;
}
.terms__agree input {
  flex: none;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  accent-color: rgb(var(--v-theme-primary));
  cursor: pointer;
}
.terms__accept {
  font-weight: 600;
}
.terms__decline {
  margin-top: 6px;
  font-size: 12px;
  color: var(--sh-text-60);
}
.terms__confirm {
  padding: 12px;
  border: 1px solid rgba(var(--v-theme-error), 0.4);
  border-radius: 8px;
  background: rgba(var(--v-theme-error), 0.06);
  font-size: 13px;
}
.terms__confirm p {
  margin: 0 0 10px;
}
.terms__confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
